import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ChangelogFormDto, type ChangelogFormInterface } from '@zaoblako/shared/modules/system/dtos/changelog/changelog-form.dto';
import { ChangelogService } from '@zaoblako/web/core/services/basis/changelog.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-changelog-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    EditorComponent,
    DatepickerComponent,
    ButtonModule,
    SpinnerComponent
  ],
  templateUrl: './changelog-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangelogFormComponent extends BaseFormComponent<ChangelogFormInterface> implements OnInit {
  public engine: 'class-transformer' | 'serde' = 'serde';

  @Input() public readonly index: number;
  public changelog = <ChangelogFormInterface>{};
  public dto = ChangelogFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    private readonly changelogService: ChangelogService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.changelogService.findManyWithPagination({ limit: 10, offset: 0 }).subscribe((changelogs) => {
        if (changelogs?.items?.length) {
          const version = changelogs.items.sort()[0]?.version;
          const lastNumber = +version?.split('.')[2] + 1;
          this.changelog.version = `${version?.split('.')[0]}.${version?.split('.')[1]}.${lastNumber}`;
          this.changelog = { ...this.changelog };
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.changelogService
      .findOneByVersion(this.formStoreService.getState.forms[this.index]?.entityId as any)
      .subscribe({
        next: (changelog) => {
          this.changelog = changelog;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveChangelog() {
    if (this.checkValidate(this.changelog).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.changelogService
          .createOrUpdateOne(this.formStoreService.getState.forms[this.index]?.entityId as any, this.changelog)
          .subscribe({
            next: (changelog) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Версия "${this.changelog.version}" изменена`);
              this.formStoreService.saveNext(changelog, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.changelogService.createOrUpdateOne(this.changelog.version, this.changelog).subscribe({
          next: (changelog) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Версия создана');
            this.formStoreService.saveNext(changelog, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteChangelog() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить версию "${this.changelog.version}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.changelogService
          .deleteOneByVersion(this.formStoreService.getState.forms[this.index]?.entityId as any)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Версия "${this.changelog.version}" удалена`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }
}
